'use client';

import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

import { PRODUCT_ANCHORS } from '@eliadmin/core/src/components/nt/product-detail/types';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { useDetailsTabs, type DetailsTabKey } from './tabs-state';

export type DetailsTab = { key: DetailsTabKey; label: string; panel: ReactNode };

/** Figma „Tab“: Body/L #363D36, 16 px sides, the active one Body/L Bold with a 2 px green underline. */
const TAB =
  'shrink-0 whitespace-nowrap rounded-t-[8px] border-b-2 px-4 py-3 transition-colors focus-visible:shadow-focus focus-visible:outline-none';
const ACTIVE = 'nt-body-l-bold border-[#127A26] text-[#0D110D]';
const INACTIVE = 'nt-body-l border-transparent text-[#363D36] hover:text-[#0D110D]';
/**
 * A panel is the target of its anchor: the margin keeps the tab bar (49 px + 40 px gap) and 24 px
 * above it in view, where the section itself used to be the target.
 */
const PANEL = 'scroll-mt-[113px] rounded-2xl focus-visible:shadow-focus focus-visible:outline-none';

/** The panel id of each tab – `#hodnotenia` in the buy box opens the reviews. */
const PANEL_IDS: Record<DetailsTabKey, string> = {
  description: PRODUCT_ANCHORS.description,
  compatibility: PRODUCT_ANCHORS.compatibility,
  reviews: PRODUCT_ANCHORS.reviews,
  downloads: PRODUCT_ANCHORS.downloads,
};
/** Anchors that need a tab shown first: the panels and the compatibility list in the description. */
const HASH_TABS: Record<string, DetailsTabKey> = {
  [`#${PRODUCT_ANCHORS.description}`]: 'description',
  [`#${PRODUCT_ANCHORS.compatibility}`]: 'compatibility',
  [`#${PRODUCT_ANCHORS.reviews}`]: 'reviews',
  [`#${PRODUCT_ANCHORS.downloads}`]: 'downloads',
};

/**
 * Figma „Tabs“ (114:1312, 146:1375, 146:8681): „Popis a parametre“, „Hodnotenia (n)“ and „Na
 * stiahnutie (n)“ switch the panel below – WAI-ARIA tabs with automatic activation; the state lives
 * in `DetailsTabsProvider`, so the sections of one tab follow it. A link to a panel or into one
 * (`#hodnotenia`, `#kompatibilita` …) shows that tab and scrolls to the target. With one panel
 * only, the bar shows its label without tab semantics.
 */
export function DetailsTabs({
  tabs,
  label,
}: {
  tabs: DetailsTab[];
  /** Accessible name of the tablist. */
  label: string;
}) {
  const { active, setActive } = useDetailsTabs();
  const current = Math.max(
    0,
    tabs.findIndex(tab => tab.key === active),
  );
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const activeRef = useRef(tabs[current]?.key);
  activeRef.current = tabs[current]?.key;
  const interactive = tabs.length > 1;
  const keys = tabs.map(tab => tab.key).join();

  useEffect(() => {
    if (!interactive) return;
    const available = new Set(keys.split(','));
    // The browser cannot scroll to an anchor in a hidden panel: show it, then scroll.
    const show = (hash: string) => {
      const key = HASH_TABS[hash];
      if (!key || !available.has(key) || activeRef.current === key) return false;
      setActive(key);
      requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView());
      return true;
    };
    const onHashChange = () => show(window.location.hash);
    // A link to the hash already in the URL fires no `hashchange` (second „Overiť kompatibilitu“).
    const onClick = (event: MouseEvent) => {
      const link = (event.target as Element | null)?.closest?.('a[href^="#"]');
      const hash = link?.getAttribute('href') ?? '';
      if (hash === window.location.hash && show(hash)) event.preventDefault();
    };
    onHashChange();
    window.addEventListener('hashchange', onHashChange);
    document.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      document.removeEventListener('click', onClick);
    };
  }, [interactive, keys, setActive]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = tabs.length - 1;
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = current === last ? 0 : current + 1;
    else if (event.key === 'ArrowLeft') next = current === 0 ? last : current - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    setActive(tabs[next].key);
    buttons.current[next]?.focus();
  };

  if (!tabs[0]) return null;

  return (
    <div className="flex flex-col gap-10">
      <div className="border-b border-[#E2E5DF]">
        <div className="hide-scroll-bar -mx-4 flex overflow-x-auto px-4 max-md:-mt-1 max-md:pt-1 sm:-mx-6 sm:px-6 md:mx-0 md:overflow-visible md:px-0">
          {interactive ? (
            <div role="tablist" aria-label={label} onKeyDown={onKeyDown} className="contents">
              {tabs.map((tab, index) => (
                <button
                  key={tab.key}
                  ref={node => {
                    buttons.current[index] = node;
                  }}
                  type="button"
                  role="tab"
                  id={`${PANEL_IDS[tab.key]}-zalozka`}
                  aria-selected={index === current}
                  aria-controls={PANEL_IDS[tab.key]}
                  tabIndex={index === current ? 0 : -1}
                  onClick={() => setActive(tab.key)}
                  className={cn(TAB, index === current ? ACTIVE : INACTIVE)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ) : (
            <p className={cn(TAB, ACTIVE)}>{tabs[0].label}</p>
          )}
        </div>
      </div>
      {interactive ? (
        tabs.map((tab, index) => (
          <div
            key={tab.key}
            role="tabpanel"
            id={PANEL_IDS[tab.key]}
            aria-labelledby={`${PANEL_IDS[tab.key]}-zalozka`}
            hidden={index !== current}
            tabIndex={0}
            className={PANEL}
          >
            {tab.panel}
          </div>
        ))
      ) : (
        <div id={PANEL_IDS[tabs[0].key]} className={PANEL}>
          {tabs[0].panel}
        </div>
      )}
    </div>
  );
}
